import { getFingerprint } from './getFingerprint';

const IP_TIMEOUT_MS = 5000;
const FINGERPRINT_TIMEOUT_MS = 8000;

const IP_PROVIDERS = [
  {
    name: 'ipify',
    url: 'https://api.ipify.org?format=json',
    readIp: (data) => data?.ip,
  },
  {
    name: 'ipwho',
    url: 'https://ipwho.is/',
    readIp: (data) => (data?.success === false ? null : data?.ip),
  },
];

const FINGERPRINT_ATTEMPTS = [
  { strategy: 'full', excludes: {} },
  {
    strategy: 'reduced',
    excludes: {
      audio: true,
      fonts: true,
      enumerateDevices: true,
      pixelRatio: true,
      doNotTrack: true,
      fontsFlash: true,
      adBlock: true,
    },
  },
];

function classifyError(error) {
  const message = error?.message || '';
  if (error?.name === 'AbortError' || error?.name === 'TimeoutError' || /timeout/i.test(message)) {
    return 'timeout';
  }
  if (/^HTTP \d+/.test(message)) return 'http';
  return 'network';
}

async function fetchWithTimeout(url, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return await response.json();
  } finally {
    clearTimeout(timer);
  }
}

function readIpString(readIp, data) {
  const value = readIp(data);
  if (typeof value !== 'string') return '';
  return value.trim();
}

function resolveIp() {
  const steps = [];

  const tryProvider = (index) => {
    if (index >= IP_PROVIDERS.length) {
      const error = new Error('IP indisponível');
      error.steps = steps;
      return Promise.reject(error);
    }

    const provider = IP_PROVIDERS[index];
    return fetchWithTimeout(provider.url, IP_TIMEOUT_MS)
      .then((data) => {
        const ip = readIpString(provider.readIp, data);
        if (!ip) {
          steps.push({ provider: provider.name, status: 'network' });
          return tryProvider(index + 1);
        }
        steps.push({ provider: provider.name, status: 'ok' });
        return { ip, steps };
      })
      .catch((error) => {
        if (error?.steps) return Promise.reject(error);
        steps.push({ provider: provider.name, status: classifyError(error) });
        return tryProvider(index + 1);
      });
  };

  return tryProvider(0);
}

function resolveFingerprint() {
  const steps = [];

  const tryAttempt = (index) => {
    if (index >= FINGERPRINT_ATTEMPTS.length) {
      const error = new Error('fingerprint timeout');
      error.steps = steps;
      return Promise.reject(error);
    }

    const attempt = FINGERPRINT_ATTEMPTS[index];
    return getFingerprint({
      excludes: attempt.excludes,
      timeoutMs: FINGERPRINT_TIMEOUT_MS,
    })
      .then((fingerprint) => {
        steps.push({ strategy: attempt.strategy, status: 'ok' });
        return { fingerprint, steps };
      })
      .catch((error) => {
        if (error?.steps) return Promise.reject(error);
        steps.push({ strategy: attempt.strategy, status: classifyError(error) });
        return tryAttempt(index + 1);
      });
  };

  return tryAttempt(0);
}

function browserContext() {
  if (typeof navigator === 'undefined') {
    return { online: true, whatsapp: false, brave: false };
  }

  const userAgent = navigator.userAgent || '';
  return {
    online: navigator.onLine !== false,
    whatsapp: /WhatsApp/i.test(userAgent),
    brave: typeof navigator.brave !== 'undefined',
  };
}

export function buildDiagnosticCode({ ipSteps = [], fpSteps = [], context = {} }) {
  const parts = [];
  const ipOk = ipSteps.some((step) => step.status === 'ok');
  const fpOk = fpSteps.some((step) => step.status === 'ok');

  if (!ipOk) {
    if (context.online === false) {
      parts.push('OFFLINE');
    } else if (ipSteps.length > 0 && ipSteps.every((step) => step.status === 'timeout')) {
      parts.push('IP_TIMEOUT');
    } else {
      parts.push('IP_BLOCKED');
    }
  }

  if (!fpOk) {
    parts.push(fpSteps.some((step) => step.status === 'timeout') ? 'FP_TIMEOUT' : 'FP_BLOCKED');
  }

  if (context.whatsapp) parts.push('WHATSAPP');
  if (context.brave) parts.push('BRAVE');
  if (!parts.length) parts.push('PREPARE_FAILED');

  return parts.join(' · ');
}

export async function prepareSignature() {
  const context = browserContext();
  const [ipResult, fpResult] = await Promise.allSettled([resolveIp(), resolveFingerprint()]);

  const ipSteps = ipResult.status === 'fulfilled' ? ipResult.value.steps : ipResult.reason?.steps || [];
  const fpSteps = fpResult.status === 'fulfilled' ? fpResult.value.steps : fpResult.reason?.steps || [];

  if (ipResult.status === 'fulfilled' && fpResult.status === 'fulfilled') {
    return {
      ip: ipResult.value.ip,
      fingerprint: fpResult.value.fingerprint,
    };
  }

  const code = buildDiagnosticCode({ ipSteps, fpSteps, context });
  const error = new Error(code);
  error.code = code;
  error.details = { ipSteps, fpSteps, context };
  throw error;
}
